import getHasMultipleAccounts from '../utils/getHasMultipleAccounts';
import { getCalendarEntryByCalendarId, getCalendarIdByFolderId } from 'owa-calendar-cache';
import type { PickerOption } from 'owa-calendar-composite-picker';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import loc, { format } from 'owa-localize';
import {
    CalendarOption,
    ChannelOption,
    isCalendarOption,
    isChannelOption,
} from '../store/schema/CalendarChannelPickerInterfaces';
import {
    selectedCalendarFormatString,
    selectedChannelFormatStringMultiAccount,
    selectedChannelFormatString,
} from '../components/CalendarChannelPicker.locstring.json';

/**
 * Format the selected calendar name - CalendarName (account)
 * @param event
 * @param pickerOption
 */
export default function formatSelectedOptionName(
    event: CalendarEvent,
    pickerOption: PickerOption
): string {
    if (!pickerOption) {
        return '';
    }

    if (isCalendarOption(pickerOption)) {
        return formatSelectedCalendarName(event, pickerOption as CalendarOption);
    }

    if (isChannelOption(pickerOption)) {
        return formatSelectedChannelName(pickerOption as ChannelOption);
    }

    return '';
}

/**
 * Format the selected calendar name - CalendarName (account)
 * @param event
 * @param calendarOption
 */
export function formatSelectedCalendarName(
    event: CalendarEvent,
    calendarOption: CalendarOption
): string {
    const hasMultipleAccounts = getHasMultipleAccounts();

    if (calendarOption?.calendar) {
        if (hasMultipleAccounts) {
            return format(
                loc(selectedCalendarFormatString),
                calendarOption.calendar.CalendarName,
                calendarOption.account
            );
        } else {
            return calendarOption.calendar.CalendarName || '';
        }
    }

    return formatDefaultSelectedCalendarName(event);
}

/**
 * Format the default selected calendar name - CalendarName (account)
 * @param event
 */
export function formatDefaultSelectedCalendarName(event: CalendarEvent): string {
    const hasMultipleAccounts = getHasMultipleAccounts();

    const calendarId = getCalendarIdByFolderId(event.ParentFolderId.Id);
    if (!calendarId) {
        return '';
    }

    const calendar = getCalendarEntryByCalendarId(calendarId.id);
    if (!calendar) {
        return '';
    }

    const calendarName = calendar.CalendarName;
    if (!calendarName) {
        return '';
    }

    if (hasMultipleAccounts) {
        const account = calendar.calendarId.mailboxInfo.userIdentity;

        return account
            ? format(loc(selectedCalendarFormatString), calendarName, account)
            : calendarName;
    }

    return calendarName;
}

/**
 * Format the selected channel name - TeamName > ChannelName (account)
 * @param channel
 */
export function formatSelectedChannelName(channel: ChannelOption): string {
    const hasMultipleAccounts = getHasMultipleAccounts();

    if (channel?.displayName) {
        return hasMultipleAccounts
            ? format(
                  loc(selectedChannelFormatStringMultiAccount),
                  channel.parentTeamName,
                  channel.displayName,
                  channel.account
              )
            : format(loc(selectedChannelFormatString), channel.parentTeamName, channel.displayName);
    }

    return '';
}
